import { useEffect, useRef, useState } from 'react'
import type ApiFormResponseType from '@/Shared/Component/Forms/ApiFormResponseType'
import { TURNSTILE_SCRIPT_ELEMENT_ID } from '@/Shared/AutoGenerated/Main'

interface TurnstileJsSdk {
    render(
        container: HTMLElement,
        options: {
            sitekey: string
            theme: 'light'
            callback: () => void
            'error-callback': () => void
            'expired-callback': () => void
            'timeout-callback': () => void
        },
    ): string
    remove(widgetId: string): void
    reset(widgetId: string): void
}

declare global {
    interface Window {
        turnstile?: TurnstileJsSdk
    }
}

interface TurnstileState {
    readonly enabled: boolean
    readonly isReady: boolean
    setReady(isReady: boolean): void
}

export function useTurnstile(enabled: boolean): TurnstileState {
    const [isReady, setReady] = useState(false)

    return {
        enabled,
        isReady: !enabled || isReady,
        setReady,
    }
}

interface TurnstileProps {
    turnstile: TurnstileState
    siteKey: string
    response: ApiFormResponseType | null
}

export function Turnstile({ turnstile, siteKey, response }: TurnstileProps) {
    const { enabled, setReady } = turnstile
    const containerRef = useRef<HTMLDivElement>(null)
    const widgetIdRef = useRef<string | null>(null)

    useEffect(() => {
        setReady(false)

        if (!enabled) {
            return
        }

        function renderWidget(): void {
            if (
                !containerRef.current ||
                !window.turnstile ||
                widgetIdRef.current !== null
            ) {
                return
            }

            widgetIdRef.current = window.turnstile.render(
                containerRef.current,
                {
                    sitekey: siteKey,
                    theme: 'light',
                    callback: () => setReady(true),
                    'error-callback': () => setReady(false),
                    'expired-callback': () => setReady(false),
                    'timeout-callback': () => setReady(false),
                },
            )
        }

        const script = document.getElementById(TURNSTILE_SCRIPT_ELEMENT_ID)

        if (!script) {
            throw new Error('Turnstile is required')
        } else {
            script.addEventListener('load', renderWidget)
            renderWidget()
        }

        return () => {
            setReady(false)
            script.removeEventListener('load', renderWidget)

            if (widgetIdRef.current !== null) {
                window.turnstile?.remove(widgetIdRef.current)
                widgetIdRef.current = null
            }
        }
    }, [enabled, siteKey, setReady])

    useEffect(() => {
        if (response !== null && widgetIdRef.current !== null) {
            setReady(false)
            window.turnstile?.reset(widgetIdRef.current)
        }
    }, [response, setReady])

    if (!enabled) {
        return null
    }

    return <div ref={containerRef} />
}
